/** UA2 frames the app as one of its own tabs. */
export const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

/** Message to the frame owner (UA2) with the content height; UA2 sizes the frame to it. */
export const FRAME_HEIGHT_MESSAGE = 'zbAdmin:height';

/**
 * In UA2 the page scrolls, not the app (as ZennoProxy): the app takes its content height and reports it,
 * so UA2 grows the frame and keeps a single scrollbar.
 */
export function reportHeightToParent(content: HTMLElement): () => void {
  let last = 0;
  const send = () => {
    const height = Math.ceil(content.getBoundingClientRect().height);
    if (height === last) return;
    last = height;
    // Only a number goes out, so any owner may read it.
    window.parent.postMessage({ type: FRAME_HEIGHT_MESSAGE, height }, '*');
  };
  const observer = new ResizeObserver(send);
  observer.observe(content);
  send();
  return () => observer.disconnect();
}

/** Message to the frame owner: bring this offset of the frame into view. */
export const FRAME_SCROLL_MESSAGE = 'zbAdmin:scrollTo';

const scrollParentTo = (top: number) => window.parent.postMessage({ type: FRAME_SCROLL_MESSAGE, top }, '*');

/** antd keeps a closed dialog's DOM and hides its wrap inline. */
const visibleModalWraps = () =>
  new Set([...document.querySelectorAll<HTMLElement>('.ant-modal-wrap')].filter(w => w.style.display !== 'none'));

/**
 * A modal sits at the top of the (tall) frame while the page may be scrolled far below it. Each time a dialog
 * becomes visible (antd reuses a closed dialog's DOM, so this is not the same as being added), the frame owner
 * scrolls the page to it. When the last dialog closes, it scrolls back to the element the manager acted on.
 */
export function revealModalsInParent(): () => void {
  let shown = new Set<HTMLElement>();
  let lastActionTop: number | null = null;
  let returnTop: number | null = null;

  // Measured on the action itself: the trigger (a menu item, a button) may be gone when the dialog closes.
  const rememberAction = (e: Event) => {
    if (e.target instanceof Element) lastActionTop = Math.round(e.target.getBoundingClientRect().top);
  };

  const update = () => {
    const now = visibleModalWraps();
    if (shown.size === 0 && now.size > 0) returnTop = lastActionTop;
    if (shown.size > 0 && now.size === 0 && returnTop !== null) {
      scrollParentTo(returnTop);
      returnTop = null;
    }
    for (const wrap of now) {
      if (shown.has(wrap)) continue;
      // After antd places the dialog.
      requestAnimationFrame(() => {
        const modal = wrap.querySelector<HTMLElement>('.ant-modal');
        if (modal && wrap.isConnected && wrap.style.display !== 'none')
          scrollParentTo(Math.round(modal.getBoundingClientRect().top));
      });
    }
    shown = now;
  };

  const observer = new MutationObserver(update);
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  document.addEventListener('click', rememberAction, true);
  document.addEventListener('keydown', rememberAction, true);
  return () => {
    observer.disconnect();
    document.removeEventListener('click', rememberAction, true);
    document.removeEventListener('keydown', rememberAction, true);
  };
}

/** Message from the frame owner: UA2 switched its language; the app follows without reloading. */
export const FRAME_LOCALE_MESSAGE = 'zbAdmin:locale';

export function listenToParentLocale(onLocale: (tag: string) => void): () => void {
  const onMessage = (e: MessageEvent) => {
    // Only the frame owner may switch the language.
    if (e.source !== window.parent || e.data?.type !== FRAME_LOCALE_MESSAGE) return;
    if (typeof e.data.locale === 'string') onLocale(e.data.locale);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
